import { useState, useRef, useCallback } from "react";
import { Drawer } from "./Drawer";
import { Button } from "../atoms/Button";
import type { RfpOwnerUser } from "../../types/rfp";

export type UploadRfpFormData = {
  file: File;
  dueDate: string;
  ownerId: string;
};

export type UploadKbDocumentFormData = {
  file: File;
};

type UploadRfpDrawerBaseProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly owners?: readonly RfpOwnerUser[];
  readonly ownersLoading?: boolean;
  readonly ownersError?: string | null;
};

type UploadRfpDrawerProps =
  | (UploadRfpDrawerBaseProps & {
      readonly mode?: "rfp";
      readonly onUpload: (formData: UploadRfpFormData) => Promise<void> | void;
    })
  | (UploadRfpDrawerBaseProps & {
      readonly mode: "kbDocument";
      readonly onUpload: (formData: UploadKbDocumentFormData) => Promise<void> | void;
    });

const MAX_SIZE_MB = 50;
/** RFP upload: align with backend rfp_supported_extensions (PDF/DOC/DOCX + XLSX/CSV). */
const RFP_ACCEPTED_EXTENSIONS = new Set([".pdf", ".doc", ".docx", ".xlsx", ".csv"]);
const RFP_ACCEPTED_TYPES = new Set([
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/csv",
  "application/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);
const KB_ACCEPTED_EXTENSIONS = new Set([".xls", ".xlsx", ".csv"]);
const RFP_ACCEPT_ATTRIBUTE = ".pdf,.doc,.docx,.xlsx,.csv";
const KB_DOCUMENT_ACCEPT_ATTRIBUTE = ".xls,.xlsx,.csv";

const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0) return "";
  return fileName.slice(dotIndex).toLowerCase();
};

export const UploadRfpDrawer = (props: UploadRfpDrawerProps) => {
  const { isOpen, onClose, owners = [], ownersLoading = false, ownersError = null } = props;
  const mode = props.mode ?? "rfp";
  const isRfpMode = mode === "rfp";
  const acceptAttribute = isRfpMode ? RFP_ACCEPT_ATTRIBUTE : KB_DOCUMENT_ACCEPT_ATTRIBUTE;
  const helperText = isRfpMode
    ? `PDF, DOC, DOCX, XLSX, CSV up to ${MAX_SIZE_MB}MB`
    : `XLS, XLSX, CSV up to ${MAX_SIZE_MB}MB`;
  const today = new Date();
  const minDueDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [file, setFile] = useState<File | null>(null);
  const [dueDate, setDueDate] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setFile(null);
    setDueDate("");
    setOwnerId("");
    setIsSubmitting(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const validateFile = useCallback(
    (f: File): string | null => {
      if (isRfpMode) {
        const extension = getFileExtension(f.name);
        const allowedByExt = RFP_ACCEPTED_EXTENSIONS.has(extension);
        const allowedByMime = RFP_ACCEPTED_TYPES.has(f.type);
        if (!allowedByExt && !allowedByMime) {
          return "Unsupported format. Please upload a PDF, DOC, DOCX, XLSX, or CSV file.";
        }
      } else {
        const extension = getFileExtension(f.name);
        if (!KB_ACCEPTED_EXTENSIONS.has(extension)) {
          return "Unsupported format. Please upload an XLS, XLSX, or CSV file.";
        }
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        return `File exceeds ${MAX_SIZE_MB}MB limit.`;
      }
      return null;
    },
    [isRfpMode],
  );

  const handleFile = useCallback(
    (f: File) => {
      const validationError = validateFile(f);
      if (validationError) {
        setError(validationError);
        setFile(null);
        return;
      }
      setError(null);
      setFile(f);
    },
    [validateFile],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0] ?? null;
      if (selected) handleFile(selected);
    },
    [handleFile],
  );

  const isValid = isRfpMode
    ? file !== null && dueDate.trim() !== "" && ownerId.trim() !== ""
    : file !== null;

  const handleSubmit = useCallback(async () => {
    if (!file || isSubmitting) return;
    setError(null);
    setIsSubmitting(true);
    try {
      if (props.mode === "kbDocument") {
        await props.onUpload({ file });
      } else {
        const normalizedDueDate = dueDate.trim();
        const normalizedOwnerId = ownerId.trim();
        if (!normalizedDueDate || !normalizedOwnerId) {
          setIsSubmitting(false);
          return;
        }
        if (normalizedDueDate < minDueDate) {
          setError("Due date cannot be earlier than today.");
          setIsSubmitting(false);
          return;
        }
        await props.onUpload({ file, dueDate: normalizedDueDate, ownerId: normalizedOwnerId });
      }
      resetState();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to upload RFP. Please try again.");
      setIsSubmitting(false);
    }
  }, [dueDate, file, isSubmitting, minDueDate, ownerId, props, resetState]);

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} title="Upload RFP">
      <div className="flex h-full flex-col">
        <div className="flex-1 space-y-4">
          {/* Upload Document */}
          <div>
            <label
              htmlFor="rfp-upload-input"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Upload Document <span className="text-status-error">*</span>
            </label>
            {file ? (
              <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 px-6 py-8">
                <div className="flex flex-col items-center text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mb-3 h-10 w-10 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                    />
                  </svg>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setError(null);
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                    className="mt-3 text-sm font-medium text-primary-500 transition-colors hover:text-primary-600"
                  >
                    Remove file
                  </button>
                </div>
              </div>
            ) : (
              <label
                htmlFor="rfp-upload-input"
                className="flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-gray-300 bg-white px-6 py-10 text-center transition-colors"
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7 text-primary-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                  </svg>
                </div>
                <p className="mb-1 text-sm text-gray-600">Click to upload your document.</p>
                <p className="text-xs text-gray-400">{helperText}</p>
              </label>
            )}
            {error && <p className="mt-2 text-sm text-status-error">{error}</p>}
          </div>

          {isRfpMode && (
            <>
              {/* Due Date */}
              <div>
                <label
                  htmlFor="rfp-due-date"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Due Date <span className="text-status-error">*</span>
                </label>
                <div className="relative">
                  <input
                    id="rfp-due-date"
                    type="date"
                    value={dueDate}
                    min={minDueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
                    />
                  </svg>
                </div>
              </div>

              {/* Owner */}
              <div>
                <label htmlFor="rfp-owner" className="mb-2 block text-sm font-medium text-gray-700">
                  Owner <span className="text-status-error">*</span>
                </label>
                <select
                  id="rfp-owner"
                  value={ownerId}
                  onChange={(e) => setOwnerId(e.target.value)}
                  disabled={ownersLoading}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  <option value="">
                    {ownersLoading ? "Loading owners..." : "Search and select owner..."}
                  </option>
                  {owners.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.email})
                    </option>
                  ))}
                </select>
                {ownersError && <p className="mt-2 text-sm text-status-error">{ownersError}</p>}
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? "Uploading..." : "Upload RFP"}
          </Button>
        </div>

        <input
          ref={inputRef}
          id="rfp-upload-input"
          type="file"
          accept={acceptAttribute}
          onChange={handleChange}
          className="hidden"
          tabIndex={-1}
        />
      </div>
    </Drawer>
  );
};
